import { useEffect, useState } from 'react';
import { api } from '../api/client';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import { Loading, ErrorState } from '../components/States';

export default function KVMSettings() {
  const [config, setConfig] = useState({
    enabled: false,
    serviceUrl: '',
    region: 'kvm-default',
  });
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadKVMConfig();
  }, []);

  const loadKVMConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/kvm/config');
      setConfig(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load KVM configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setMessage(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setMessage(null);

      if (!config.serviceUrl && config.enabled) {
        setError('Service URL is required when KVM is enabled');
        return;
      }

      const response = await api.post('/admin/kvm/config', config);
      setMessage(response.data.message || 'Configuration saved successfully');
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError(null);

      if (!config.serviceUrl) {
        setError('Service URL is required');
        return;
      }

      const response = await api.post('/admin/kvm/test-connection', {
        serviceUrl: config.serviceUrl,
      });

      setTestResult(response.data);
      if (!response.data.success) {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to test connection');
      setTestResult({
        success: false,
        message: err.message,
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Loading label="Loading KVM configuration..." />;

  return (
    <div>
      <PageHeader
        title="KVM Hypervisor Settings"
        detail="Configure connection to remote KVM microservice"
      />

      {error && (
        <Card variant="error" title="Error" detail={error} />
      )}

      {message && (
        <Card variant="success" title="Success" detail={message} />
      )}

      <Card title="KVM Service Configuration">
        <form className="settings-form">
          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                name="enabled"
                checked={config.enabled}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              Enable KVM Integration
            </label>
            <p className="form-help">
              Enable this to manage virtual machines through the KVM microservice
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Service URL</label>
            <input
              type="text"
              name="serviceUrl"
              value={config.serviceUrl}
              onChange={handleInputChange}
              placeholder="http://localhost:8081"
              className="form-input"
              disabled={!config.enabled}
            />
            <p className="form-help">
              Base URL of the KVM microservice (e.g., http://localhost:8081 or http://kvm-host:8081)
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">Region</label>
            <input
              type="text"
              name="region"
              value={config.region}
              onChange={handleInputChange}
              placeholder="kvm-default"
              className="form-input"
              disabled={!config.enabled}
            />
            <p className="form-help">
              Region identifier for this KVM host
            </p>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={!config.enabled || !config.serviceUrl || testing || saving}
              className="btn btn-secondary"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </form>
      </Card>

      {testResult && (
        <Card
          title="Connection Test Result"
          variant={testResult.success ? 'success' : 'error'}
        >
          <p>{testResult.message}</p>
          <pre className="status-detail">
            {JSON.stringify(testResult, null, 2)}
          </pre>
        </Card>
      )}

      <Card title="Configuration Reference">
        <div className="reference-section">
          <h3>Connection URL Format</h3>
          <p>
            The KVM microservice can run locally or on a remote host. Use the appropriate format:
          </p>
          <ul>
            <li><strong>Local:</strong> http://localhost:8081</li>
            <li><strong>Remote:</strong> http://kvm-host.example.com:8081</li>
            <li><strong>Custom Port:</strong> http://192.168.1.100:8081</li>
          </ul>
        </div>

        <div className="reference-section">
          <h3>Remote SSH Connection to KVM Host</h3>
          <p>
            If using SSH to connect to a remote KVM host, configure the KVM microservice with:
          </p>
          <pre className="code-block">
KVM_URI=qemu+ssh://root@192.168.1.100/system
          </pre>
          <p>
            See the KVM Remote Connection Guide for detailed setup instructions.
          </p>
        </div>

        <div className="reference-section">
          <h3>After Configuration</h3>
          <ol>
            <li>Enter the service URL and test the connection</li>
            <li>Save the configuration</li>
            <li>Restart the KVM microservice if needed</li>
            <li>KVM services will appear in the Services page</li>
          </ol>
        </div>
      </Card>

      <style>{`
        .settings-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          font-size: 0.95rem;
        }

        .form-checkbox {
          cursor: pointer;
          width: 1rem;
          height: 1rem;
        }

        .form-input {
          padding: 0.75rem;
          border: 1px solid #ccc;
          border-radius: 0;
          font-size: 0.95rem;
          font-family: inherit;
        }

        .form-input:disabled {
          background-color: #f5f5f5;
          color: #999;
          cursor: not-allowed;
        }

        .form-help {
          font-size: 0.85rem;
          color: #666;
          margin: 0;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 0;
          font-size: 0.95rem;
          cursor: pointer;
          font-weight: 500;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background-color: #0066cc;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0052a3;
        }

        .btn-secondary {
          background-color: #f0f0f0;
          color: #333;
          border: 1px solid #ccc;
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        .status-detail {
          background-color: #f5f5f5;
          padding: 1rem;
          border-radius: 0;
          font-size: 0.85rem;
          overflow-x: auto;
          margin: 1rem 0 0 0;
        }

        .reference-section {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #eee;
        }

        .reference-section h3 {
          margin-top: 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .reference-section p {
          margin: 0.5rem 0;
          color: #666;
          font-size: 0.95rem;
        }

        .reference-section ul,
        .reference-section ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }

        .reference-section li {
          margin: 0.5rem 0;
          color: #666;
          font-size: 0.95rem;
        }

        .code-block {
          background-color: #f5f5f5;
          padding: 1rem;
          border-radius: 0;
          font-size: 0.85rem;
          overflow-x: auto;
          margin: 1rem 0;
          font-family: 'Courier New', monospace;
        }
      `}</style>
    </div>
  );
}
